import { memo, useMemo } from 'react';
import classNames from 'classnames';

import { Button } from '../../button/button.component';
import { CardV2LayoutStyle } from '../../card-v2/card-v2.component';
import { ImageEmbedded } from '../../image/image-embedded';
import { Link, LinkModel } from '../../link/link';
import Svg from '../../svg/svg.component';
import TypographyV2 from '../../typographyv2/typographyv2';

import { NavPromoCardIsEmpty, NavPromoCardProps } from './nav-promo-card.config';

const NavPromoCard = memo(
  ({
    additionalClasses,
    cqPath,
    description,
    headline,
    image,
    isInEditor,
    layout,
    linkTitle,
    linkUrl,
    openInNewWindow,
    propertiesId,
    propertiesLabel,
    propertiesRole,
  }: NavPromoCardProps) => {
    const isHorizontal = layout === CardV2LayoutStyle.HORIZONTAL;
    const link: LinkModel = useMemo(
      () => ({ url: linkUrl, attributes: { target: openInNewWindow ? '_blank' : '_self' } }),
      [linkUrl, openInNewWindow]
    );

    const renderedImage = useMemo(
      () =>
        image ? (
          <ImageEmbedded
            baseCssClass="snowflake-mega-nav-nav-promo-card-image"
            dataTestID="snowflake-mega-nav-nav-promo-card-image"
            {...image}
          />
        ) : null,
      [image]
    );

    const promoCardLink = useMemo(
      () =>
        !isHorizontal ? (
          <div
            className="snowflake-mega-nav-nav-promo-card-link snowflake-button-blue snowflake-button-link snowflake-button-regular"
            data-testid="snowflake-mega-nav-nav-promo-card-link"
          >
            <Button
              appliedCssClassNames="snowflake-button-link"
              text={linkTitle}
              showOutboundIcon={openInNewWindow}
              disableAnchor
              buttonLink={link}
              tabIndex={-1}
            />
          </div>
        ) : null,
      [isHorizontal, link, linkTitle, openInNewWindow]
    );

    const promoCardLinkIcon = useMemo(
      () =>
        isHorizontal ? (
          <Svg
            name="caret-down"
            className="snowflake-mega-nav-nav-promo-card-link-icon"
            data-testid="snowflake-mega-nav-nav-promo-card-link-icon"
          />
        ) : null,
      [isHorizontal]
    );

    return !NavPromoCardIsEmpty({ cqPath, headline, isInEditor, linkTitle, linkUrl }) ? (
      <Link
        aria-label={propertiesLabel}
        className={classNames('snowflake-mega-nav-nav-promo-card', additionalClasses, {
          'snowflake-mega-nav-nav-promo-card-horizontal': isHorizontal,
        })}
        id={propertiesId}
        link={link}
        role={propertiesRole}
        data-testid="snowflake-mega-nav-nav-promo-card"
      >
        {renderedImage}
        <div className="snowflake-mega-nav-nav-promo-card-content">
          <div className="snowflake-mega-nav-nav-promo-card-info-content">
            <TypographyV2
              className="snowflake-mega-nav-nav-promo-card-headline"
              dataTestId="snowflake-mega-nav-nav-promo-card-headline"
              variant="span"
              variantClassName={isHorizontal ? 'heading-6-v2' : 'heading-5-v2'}
            >
              {headline}
            </TypographyV2>
            {description ? (
              <TypographyV2
                className="snowflake-mega-nav-nav-promo-card-description"
                dataTestId="snowflake-mega-nav-nav-promo-card-description"
                variant="body4"
              >
                {description}
              </TypographyV2>
            ) : null}
          </div>

          {promoCardLink}
          {promoCardLinkIcon}
        </div>
      </Link>
    ) : null;
  }
);

NavPromoCard.displayName = 'NavPromoCard';

export default NavPromoCard;
